import { useLayoutEffect, useRef, useState } from 'react'

import type { WorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  OutlineDangerButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaTrashCanRegular } from '@enechain/ecloud-icons'

import { workflowCaseStatusBadgeProps } from '~/components/common/WorkflowCaseStatusBadge'
import { iconSize } from '~/constants/iconSize'
import { isWorkflowCaseDeletable } from '~/models/workflowCaseDeletable'

type Props = {
  wfCase: WorkflowCase
  doneSubSteps: number
  totalSubSteps: number
  /** 「削除」ボタンクリック時のハンドラ (spec AC-003f: 削除確認ダイアログを開く)。 */
  onDeleteClick: () => void
}

/**
 * 画面下部の action bar (design/demand/case-detail.html `.cd-action-bar` 相当)。
 *
 * 呼び出し元は PageScrollArea の**中**に置く。 本 component は `position: fixed` で
 * **ブラウザ下端**に貼り付き、 水平位置だけ場所取りの実測値で本文領域に合わせる
 * (2026-09-03 デザイナー指摘「floating bar の周りの余白は削除し、 ブラウザの下部に」。
 * それ以前は `position: sticky; bottom: 0` = #5660 Des 裁定 ① だったが、 DS の外周
 * padding ぶんの余白が左右と下に残っていた)。 本文は画面全体でスクロールしつつ CTA は
 * 常時可視、 という 8080 と同じ形である点は変わらない。
 *
 * ワークフローID・ステータス・進捗を常時可視化しつつ、右端に状態遷移 CTA を配置する。
 * 未着手・進行中=削除のみ、完了・削除済み=CTA なし (spec AC-003i、
 * workflow-delete-transition design)。完了は全サブステップ完了で自動的に
 * 遷移する (spec AC-WFW-008、手動で完了させる操作は提供しない)。削除は不可逆
 * (spec AC-003g): 削除後は CTA を表示しない (やり直しが必要な場合は新規
 * ワークフロー作成で対応する)。実処理は呼び出し元 (index.tsx) のダイアログが担う
 * (= 本コンポーネントは trigger のみ)。
 */
export const CaseActionBar = ({
  wfCase,
  doneSubSteps,
  totalSubSteps,
  onDeleteClick,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { labelKey: statusLabelKey } = workflowCaseStatusBadgeProps(
    wfCase.status,
  )

  /**
   * 場所取りの実測矩形。 高さはバーの裏に本文を隠さないため、 水平位置は fixed の
   * バーを**本文の左端に合わせる**ために使う。
   *
   * 場所取りは flow の中 (= 本来バーが在るべき位置) に残しているので、 その
   * `getBoundingClientRect()` がそのまま「本文領域の左端と幅」になる。 サイドメニューの
   * 開閉やウィンドウ幅の変化にも自動で追随する。
   *
   * バー本体ではなく場所取りを測るのが要点。 バーは fixed なので自分の位置を答えとして
   * 使えない (自己参照になる)。
   */
  const [barHeight, setBarHeight] = useState(0)
  const [bounds, setBounds] = useState<{ left: number; width: number } | null>(
    null,
  )
  const barRef = useRef<HTMLDivElement | null>(null)
  const spacerRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const bar = barRef.current
    const spacer = spacerRef.current
    if (bar === null || spacer === null) return undefined
    const sync = (): void => {
      setBarHeight(bar.getBoundingClientRect().height)
      const rect = spacer.getBoundingClientRect()
      setBounds({ left: rect.left, width: rect.width })
    }
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(bar)
    observer.observe(spacer)
    // サイドメニューの開閉はウィンドウ幅を変えずに本文左端を動かすので、
    // spacer の resize では拾えるがウィンドウの resize も併せて見る。
    window.addEventListener('resize', sync)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [])

  return (
    <>
      {/* バーは flow から抜けるので、 本文の最下部がバーの裏に隠れないよう同じ高さの
          場所取りを残す。 高さは実測する — バーは `wrap="wrap"` で狭い画面では 2 行に
          なるため、 定数で持つとその時だけ隠れる。
          親 PageScrollArea は flex column なので、 本文がはみ出すと空の flex item は
          0 まで縮む。 縮ませないことで場所取りとして成立する。 */}
      <Box
        ref={spacerRef}
        flexShrink={0}
        height={`${barHeight}px`}
        marginTop="auto"
        aria-hidden
      />
      <Box
        ref={barRef}
        /* #5660 で `position: sticky` にしていたが、 バーの左右と下に DS
           `SideMenuLayoutContentLayout` の外周 padding (16px) ぶんの余白が残っていた
           (2026-09-03 デザイナー指摘「floating bar の周りの余白は削除し、 ブラウザの
           下部に」)。

           負マージンで相殺する手 (`DemandForecastDaily/components/ConfirmBar.tsx` が
           採った形) は**この画面では使えない**。 あちらはバーが DS の padding 付き Box
           のほぼ直下にあるのに対し、 本バーは `PageBody` (overflow hidden) →
           `PageScrollArea` (overflow auto) と**クリップする祖先を 2 段挟んだ内側**に
           あるため、 負マージンは `PageScrollArea` の枠を実際にはみ出して横スクロール
           バーを生やす。 加えて `bottom: 0` の sticky は containing block の content box
           にクランプするので、 下の 16px は margin では消えない。

           そこで design と同じ `position: fixed` に寄せる (8080 は同じ理由で
           `.sticky-action-bar` / `.dft-confirm-bar` を fixed にしている)。
           DS `SideMenuLayout` の root は desktop で `position: fixed; inset: 0` だが、
           包含ブロックを作る `transform` 等は意図的に置かれていない
           (`components/layouts/MainLayout/index.tsx` のコメント) ので、 本バーの fixed は
           **viewport 基準**になる。

           `left/right: 0` で viewport 全幅に敷き、 サイドメニュー (DS z-index 1301) より
           下の `zIndex: 10` に置く。 メニューがバーの左側を塗りつぶすので、 見た目は
           本文の左端から始まる (8080 の同バーと同じ作法)。 */
        position="fixed"
        zIndex={10}
        bottom="0"
        left={bounds === null ? '0' : `${bounds.left}px`}
        width={bounds === null ? '100%' : `${bounds.width}px`}
        padding="12px 20px"
        background="Surface.primary"
        borderColor="Border.lowEmphasis"
        borderTopWidth="1px"
        boxShadow="0 -2px 8px rgba(0, 0, 0, 0.06)"
      >
        <HStack align="center" justify="space-between" wrap="wrap" gap={3}>
          <HStack
            align="center"
            wrap="wrap"
            gap={2}
            color="Text.midEmphasis"
            fontSize="sm"
          >
            <Text
              color="Text.highEmphasis"
              fontWeight="semibold"
              fontVariantNumeric="tabular-nums"
            >
              {wfCase.workflowId !== ''
                ? wfCase.workflowId
                : wfCase.id.slice(0, 8).toUpperCase()}
            </Text>
            <Text as="span" color="Text.lowEmphasis" aria-hidden="true">
              ・
            </Text>
            <Text as="span">{t(statusLabelKey)}</Text>
            <Text as="span" color="Text.lowEmphasis" aria-hidden="true">
              ・
            </Text>
            <Text as="span">
              {t('page:workflowCaseDetail.actionBar.progress', {
                done: String(doneSubSteps),
                total: String(totalSubSteps),
              })}
            </Text>
          </HStack>

          <HStack gap={2}>
            {/* design case-detail.html: 削除 CTA は ゴミ箱アイコン + 「このワークフローを
              削除」。 ラベルだけだと破壊操作だと読み取るのが遅れる
              (2026-08-17 デザイナー指摘)。 */}
            {isWorkflowCaseDeletable(wfCase.status) && (
              <OutlineDangerButton onClick={onDeleteClick} size="md">
                <FaTrashCanRegular boxSize={iconSize.labeledButton} />
                {t('page:workflowCaseDetail.action.deleteCase')}
              </OutlineDangerButton>
            )}
          </HStack>
        </HStack>
      </Box>
    </>
  )
}
